import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { FileOutput, Download, Trash2, Clock, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DocumentsPage() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/documents`, headers);
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocuments(); }, []);

  const handleDownload = (doc) => {
    // Direct browser navigation with token as query param for reliable download
    const downloadUrl = `${API}/documents/${doc.id}/download?token=${encodeURIComponent(token)}`;
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = downloadUrl;
    a.download = `document_${doc.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 200);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Document deleted");
      setDeleteTarget(null);
      fetchDocuments();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 lg:p-8" data-testid="documents-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Generated Documents
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            View and download PDFs generated via the API or manual generation
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        ) : documents.length === 0 ? (
          <Card className="border-dashed" data-testid="empty-documents">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <FileOutput size={28} className="text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-700 mb-1">No documents yet</h3>
              <p className="text-sm text-slate-500">Documents will appear here when generated via the API or the Generate page</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="documents-list">
            {documents.map((doc) => (
              <Card key={doc.id} className="hover:shadow-sm transition-shadow" data-testid={`document-card-${doc.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                      <FileText size={18} className="text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        From: <span className="font-heading">{doc.template_name}</span>
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock size={12} />
                        <span>{new Date(doc.created_at).toLocaleString()}</span>
                        <span className="mx-1">|</span>
                        <span className="font-mono text-[10px]">{doc.id.slice(0, 8)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                      <Download size={14} strokeWidth={1.5} />
                      Download
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => setDeleteTarget(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 size={14} strokeWidth={1.5} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-500" />
              Delete Document
            </DialogTitle>
            <DialogDescription>
              This will permanently delete this generated document. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-button">
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-button">
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
